// dialogs/SupplierCreateDialog.js

import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Typography,
  Alert,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import MobileDialog from './MobileDialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import API from '../../services/api';

// Define supplier fields with their labels and validation
const SUPPLIER_FIELDS = [
  {
    key: 'SupplierName',
    label: 'שם הספק',
    type: 'text',
    required: true,
    multiline: false,
  },
  {
    key: 'SupplierBusinessID',
    label: 'ח.פ./ע.מ.',
    type: 'text',
    required: false,
    multiline: false,
    helperText: 'אופציונלי',
  },
  {
    key: 'ContactPerson',
    label: 'איש קשר',
    type: 'text',
    required: false,
    multiline: false,
    helperText: 'אופציונלי',
  },
  {
    key: 'SupplierPhone',
    label: 'טלפון',
    type: 'tel',
    required: false,
    multiline: false,
    helperText: 'אופציונלי',
  },
  {
    key: 'SupplierEmail',
    label: 'אימייל',
    type: 'email',
    required: false,
    multiline: false,
    helperText: 'אופציונלי',
  },
  {
    key: 'SupplierAddress',
    label: 'כתובת',
    type: 'text',
    required: false,
    multiline: true,
    rows: 3,
    helperText: 'אופציונלי',
  },
  {
    key: 'Notes',
    label: 'הערות',
    type: 'text',
    required: false,
    multiline: true,
    rows: 3,
    helperText: 'אופציונלי',
  },
];

const CURRENCY_OPTIONS = [
  { code: 'ILS', label: '₪ שקל (ILS)', symbol: '₪' },
  { code: 'USD', label: '$ דולר (USD)', symbol: '$' },
  { code: 'EUR', label: '€ אירו (EUR)', symbol: '€' },
];

const CURRENCY_SYMBOLS = { ILS: '₪', USD: '$', EUR: '€' };

export default function SupplierCreateDialog({ open, onClose, userProfile }) {
  const queryClient = useQueryClient();
  const [createStep, setCreateStep] = useState('supplierDetails');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [currency, setCurrency] = useState('ILS');
  const [exchangeRate, setExchangeRate] = useState('');
  const errorRef = useRef(null);

  // Scroll to error when it appears
  useEffect(() => {
    if (createError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [createError]);

  const [supplierFormData, setSupplierFormData] = useState(() => {
    const init = {};
    SUPPLIER_FIELDS.forEach((field) => {
      init[field.key] = '';
    });
    return init;
  });

  // State for initial balance
  const [initialBalance, setInitialBalance] = useState('0');

  // =========== CREATE MUTATION ===========
  const createSupplierMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await API.post('/supplier/create/', payload);  // CHANGED: Use correct endpoint
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['suppliers']);
      queryClient.refetchQueries(['suppliers'], { active: true });
    },
    onError: (err) => {
      console.error('Error creating supplier:', err);
    },
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setCreateStep('supplierDetails');
      setCreateError('');
      setCreateSuccess('');

      const initSupplier = {};
      SUPPLIER_FIELDS.forEach((field) => (initSupplier[field.key] = ''));
      setSupplierFormData(initSupplier);
      setInitialBalance('0');
      setCurrency('ILS');
      setExchangeRate('');
    }
  }, [open]);

  // Fetch exchange rate when currency changes
  useEffect(() => {
    if (currency && currency !== 'ILS') {
      API.get(`/supplier/exchange-rate/?currency=${currency}`)
        .then((res) => {
          setExchangeRate(String(res.data.rate));
        })
        .catch(() => {
          setExchangeRate('');
        });
    } else {
      setExchangeRate('');
    }
  }, [currency]);

  // =========== VALIDATION & NAVIGATION ===========
  const handleSupplierDetailsNext = () => {
    setCreateError('');
    
    // Validate required fields
    for (let field of SUPPLIER_FIELDS) {
      const value = (supplierFormData[field.key] || '').trim();
      
      if (field.required && !value) {
        setCreateError(`שדה "${field.label}" הוא שדה חובה`);
        return;
      }

      // Validate email format if it's an email field and has a value
      if (field.key === 'SupplierEmail' && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          setCreateError('כתובת אימייל לא תקינה');
          return;
        }
      }
    }
    
    setCreateStep('balanceDetails');
  };

  const handleBalanceNext = () => {
    setCreateError('');
    setCreateStep('confirm');
  };

  const handleCreateSupplier = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setCreateError('');
    setCreateSuccess('');

    try {
      // Clean up empty fields
      const payload = {};
      Object.keys(supplierFormData).forEach(key => {
        const value = supplierFormData[key]?.trim();
        if (value) {
          payload[key] = value;
        }
      });

      // Add currency
      if (currency !== 'ILS') {
        payload.Currency = currency;
      }

      // Add initial balance if not zero
      const balanceValue = parseFloat(initialBalance.replace(',', '.'));
      if (!isNaN(balanceValue) && balanceValue !== 0) {
        payload.initial_balance = balanceValue;
        // Add exchange rate for non-ILS suppliers
        if (currency !== 'ILS' && exchangeRate) {
          payload.exchange_rate = parseFloat(exchangeRate);
        }
      }

      await createSupplierMutation.mutateAsync(payload);
      setCreateSuccess('הספק נוצר בהצלחה!');
      setCreateStep('done');
    } catch (err) {
      let errorMsg =
        err?.response?.data?.error ||
        err?.response?.data?.SupplierName ||
        err?.response?.data?.SupplierBusinessID ||
        'שגיאה ביצירת ספק חדש';

      if (errorMsg.includes('already exists')) {
        errorMsg = 'ספק עם פרטים אלו כבר קיים במערכת';
      }
      setCreateError(errorMsg);
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  // Helper function to format balance display
  const formatBalanceDisplay = () => {
    const balance = parseFloat(initialBalance.replace(',', '.'));
    if (isNaN(balance) || balance === 0) return 'אין יתרה';

    const formatted = new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: currency || 'ILS'
    }).format(Math.abs(balance));

    if (balance > 0) {
      return `חייב: ${formatted} (אתה חייב לספק)`;
    } else {
      return `זכות: ${formatted} (הספק חייב לך)`;
    }
  };

  // =========== RENDERING ===========
  return (
    <MobileDialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose?.();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: '1.8rem', fontWeight: 'bold', mb: 1 }}>
        יצירת ספק חדש
      </DialogTitle>

      <DialogContent dividers sx={{ fontSize: '1.2rem' }}>
        {createError && <Alert ref={errorRef} severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
        {createSuccess && <Alert severity="success" sx={{ mb: 2 }}>{createSuccess}</Alert>}

        {/* STEP 1: SUPPLIER DETAILS */}
        {createStep === 'supplierDetails' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 1 }}>
            {/* Name field first */}
            {SUPPLIER_FIELDS.filter(f => f.key === 'SupplierName').map((field) => (
              <TextField
                key={field.key}
                label={field.label}
                type={field.type}
                required={field.required}
                fullWidth
                value={supplierFormData[field.key]}
                onChange={(e) => setSupplierFormData((prev) => ({ ...prev, [field.key]: e.target.value }))}
                helperText={field.helperText}
              />
            ))}

            {/* Currency selector — right after name */}
            <FormControl fullWidth>
              <InputLabel id="currency-label">מטבע</InputLabel>
              <Select
                labelId="currency-label"
                value={currency}
                label="מטבע"
                onChange={(e) => setCurrency(e.target.value)}
              >
                {CURRENCY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.code} value={opt.code}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Remaining fields */}
            {SUPPLIER_FIELDS.filter(f => f.key !== 'SupplierName').map((field) => (
              <TextField
                key={field.key}
                label={field.label}
                type={field.type}
                required={field.required}
                fullWidth
                multiline={field.multiline}
                rows={field.rows || 1}
                value={supplierFormData[field.key]}
                onChange={(e) => setSupplierFormData((prev) => ({ ...prev, [field.key]: e.target.value }))}
                helperText={field.helperText}
              />
            ))}
          </Box>
        )}

        {/* STEP 2: BALANCE DETAILS */}
        {createStep === 'balanceDetails' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              יתרה התחלתית (אופציונלי):
            </Typography>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>חיובי:</strong> אתה חייב לספק<br />
                <strong>שלילי:</strong> הספק חייב לך
              </Typography>
            </Alert>
            <TextField
              label={`יתרה התחלתית${currency !== 'ILS' ? ` (${CURRENCY_SYMBOLS[currency] || currency})` : ''}`}
              type="number"
              fullWidth
              value={initialBalance}
              onChange={(e) => setInitialBalance(e.target.value)}
              onFocus={(e) => e.target.select()}
              helperText="הזן סכום חיובי אם אתה חייב לספק, שלילי אם הספק חייב לך"
              sx={{
                direction: 'ltr',
                '& input': {
                  textAlign: 'right',
                  // Remove spinner arrows in Chrome, Safari, Edge, Opera
                  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  // Remove spinner arrows in Firefox
                  '-moz-appearance': 'textfield',
                },
              }}
              margin="normal"
            />

            {currency !== 'ILS' && (
              <>
                <TextField
                  label={`שער חליפין (1 ${currency} = ? ₪)`}
                  type="number"
                  fullWidth
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                  helperText="שער מעודכן מבנק ישראל - ניתן לשנות"
                  sx={{
                    direction: 'ltr',
                    '& input': { textAlign: 'right' },
                  }}
                  margin="normal"
                />
                {initialBalance && exchangeRate && parseFloat(initialBalance) !== 0 && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    ≈ {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(
                      Math.abs(parseFloat(initialBalance)) * parseFloat(exchangeRate)
                    )} (שווי ב-₪)
                  </Typography>
                )}
              </>
            )}
          </Box>
        )}

        {/* STEP 3: CONFIRM DETAILS */}
        {createStep === 'confirm' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              אישור לפני יצירה
            </Typography>
            {SUPPLIER_FIELDS.map((field) => {
              const value = supplierFormData[field.key]?.trim();
              if (!value) return null;
              
              return (
                <Typography key={field.key}>
                  <strong>{field.label}:</strong> {value}
                </Typography>
              );
            })}
            
            {currency !== 'ILS' && (
              <Typography>
                <strong>מטבע:</strong> {CURRENCY_OPTIONS.find(o => o.code === currency)?.label}
              </Typography>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: 'bold' }}>יתרה התחלתית:</Typography>
              <Typography>{formatBalanceDisplay()}</Typography>
              {currency !== 'ILS' && exchangeRate && parseFloat(initialBalance) !== 0 && (
                <Typography variant="body2" color="text.secondary">
                  שער חליפין: {exchangeRate} | ≈ {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(
                    Math.abs(parseFloat(initialBalance)) * parseFloat(exchangeRate)
                  )}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* STEP 4: DONE (success) */}
        {createStep === 'done' && (
          <Typography sx={{ fontWeight: 'bold', my: 2 }}>
            הספק נוצר בהצלחה! ניתן לסגור חלון זה.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        {createStep !== 'done' && (
          <Button
            onClick={onClose}
            color="inherit"
            sx={{ fontSize: '1.1rem' }}
            disabled={isSubmitting}
          >
            ביטול
          </Button>
        )}

        {createStep === 'supplierDetails' && (
          <Button
            variant="contained"
            onClick={handleSupplierDetailsNext}
            sx={{ fontSize: '1.1rem' }}
          >
            הבא
          </Button>
        )}

        {createStep === 'balanceDetails' && (
          <>
            <Button
              color="inherit"
              onClick={() => setCreateStep('supplierDetails')}
              sx={{ fontSize: '1.1rem' }}
              disabled={isSubmitting}
            >
              חזרה
            </Button>
            <Button
              variant="contained"
              onClick={handleBalanceNext}
              sx={{ fontSize: '1.1rem' }}
            >
              הבא
            </Button>
          </>
        )}

        {createStep === 'confirm' && (
          <>
            <Button
              color="inherit"
              onClick={() => setCreateStep('balanceDetails')}
              sx={{ fontSize: '1.1rem' }}
              disabled={isSubmitting}
            >
              חזרה
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateSupplier}
              sx={{ fontSize: '1.1rem' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'אישור סופי'}
            </Button>
          </>
        )}

        {createStep === 'done' && (
          <Button
            variant="contained"
            onClick={onClose}
            sx={{ fontSize: '1.1rem' }}
          >
            סגור
          </Button>
        )}
      </DialogActions>
    </MobileDialog>
  );
}